import { Box, Checkbox, FormControl, FormControlLabel, GridLegacy as Grid } from "@mui/material";
import { Permission } from "generated-graphql/graphql";

import { PERMISSION_GROUPS } from "../constants/permissionGroups";

type PermissionCheckboxGridProps = {
  selectedPermissions: Permission[];
  onChange: (permissions: Permission[]) => void;
  disabled?: boolean;
};

/**
 * Renders the grouped permission checkboxes used to inspect or edit a set of
 * permissions. Clicking a group header toggles every permission in the group.
 * Mirrors the layout of the Emulate Permissions dialog.
 */
export function PermissionCheckboxGrid({
  selectedPermissions,
  onChange,
  disabled,
}: PermissionCheckboxGridProps) {
  const togglePermission = (permission: Permission) => {
    const next = selectedPermissions.includes(permission)
      ? selectedPermissions.filter((p) => p !== permission)
      : [...selectedPermissions, permission];
    onChange(next);
  };

  const toggleGroup = (group: string) => {
    const permissions = PERMISSION_GROUPS[group];
    const allSelected = permissions.every((permission) => selectedPermissions.includes(permission));
    const next = allSelected
      ? selectedPermissions.filter((p) => !permissions.includes(p))
      : [...selectedPermissions, ...permissions.filter((p) => !selectedPermissions.includes(p))];
    onChange(next);
  };

  return (
    <Grid container spacing={2}>
      {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => (
        <Grid item xs={6} key={group}>
          <FormControl component="fieldset" variant="standard">
            <Box
              fontWeight="fontWeightBold"
              mb={1}
              onClick={() => !disabled && toggleGroup(group)}
              sx={{ cursor: disabled ? "default" : "pointer" }}
            >
              {group}
            </Box>
            {permissions.map((permission) => (
              <FormControlLabel
                key={permission}
                control={
                  <Checkbox
                    checked={selectedPermissions.includes(permission)}
                    onChange={() => togglePermission(permission)}
                    disabled={disabled}
                  />
                }
                label={permission}
              />
            ))}
          </FormControl>
        </Grid>
      ))}
    </Grid>
  );
}
